import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { tienePermisosModulo } from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx } from 'components/financepro';
import { useProductosMaterialContext } from 'context';
import PrintListado from 'utils/PrintListado';
import { ETIQUETA_VERSION, useVersionContable } from 'contabilidad/versionContable';
import {
  btnOutlinedSx,
  btnPrimarySx,
  cardShellSx,
  cellEllipsisSx,
  cellHeadSx,
  fechaLocalHoy,
  filterHeaderSx,
  formatearFecha,
  formatearFechaLarga,
  fmtMoney,
  fmtMontoContable,
  primerDiaMesActual,
} from './estadosFinancierosShared';

/**
 * Shell reutilizable para estados financieros.
 * Seguridad alineada a FacturaPOS: tienePermisosModulo(programa) para
 * lectura/acción, credenciales usuario/clave y empresa con db.
 */
const EstadoFinancieroShell = ({
  programa,
  title,
  subtitle,
  reportTitle,
  IconComponent,
  endpoint,
  columns,
  emptyMessage = 'No hay datos para el período seleccionado',
  mapResponseRows,
  footerFromRows,
  renderSummary,
  renderBody,
  hideFooter = false,
  buildPrintHtml,
}) => {
  const { controller, selectedCompany, showError, showSuccess, showWarning } =
    useProductosMaterialContext();
  const { nombreNegocio, usuarioLog, claveLog, roleDetalles } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';
  const { activa: versionesActivas, version } = useVersionContable();
  const etiquetaVersion = versionesActivas ? `Versión ${ETIQUETA_VERSION[version].toLowerCase()}` : '';

  const puedeLeer = useMemo(
    () => tienePermisosModulo(roleDetalles, programa),
    [roleDetalles, programa]
  );

  const puedeModificar = useMemo(
    () => tienePermisosModulo(roleDetalles, programa),
    [roleDetalles, programa]
  );

  const puedeOperar = Boolean(selectedCompany?.db_empresa && usuarioLog && claveLog);
  const puedeAccionar = Boolean(puedeModificar && usuarioLog && puedeOperar);

  const [fechaDesde, setFechaDesde] = useState(primerDiaMesActual);
  const [fechaHasta, setFechaHasta] = useState(fechaLocalHoy);
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [generado, setGenerado] = useState(false);
  const [infoServicio, setInfoServicio] = useState('');

  const nombreEmpresa =
    selectedCompany?.name || selectedCompany?.empresa || nombreNegocio || 'Empresa';

  const credencialesBase = useCallback(
    () => ({
      id_empresa: selectedCompany,
      usuario: usuarioLog || '',
      password: claveLog || '',
      version,
    }),
    [selectedCompany, usuarioLog, claveLog, version]
  );

  // Un reporte generado en una versión no vale para la otra.
  useEffect(() => {
    setFilas([]);
    setGenerado(false);
    setInfoServicio('');
  }, [version]);

  const subtituloPrint = useMemo(() => {
    const fd = fechaDesde ? `Desde ${formatearFecha(fechaDesde)}` : '';
    const fh = fechaHasta ? `Hasta ${formatearFecha(fechaHasta)}` : '';
    return [etiquetaVersion, fd, fh, `${filas.length} línea(s)`].filter(Boolean).join(' · ');
  }, [fechaDesde, fechaHasta, filas.length, etiquetaVersion]);

  const columnasPrint = useMemo(
    () =>
      (columns || []).map((c) => ({
        id: c.id,
        label: c.label,
        align: c.align || 'left',
        render: c.printRender || c.render,
      })),
    [columns]
  );

  const footerTexto = useMemo(() => {
    if (typeof footerFromRows === 'function') return footerFromRows(filas) || '';
    return '';
  }, [filas, footerFromRows]);

  const printDocumento = useMemo(() => {
    if (typeof buildPrintHtml !== 'function' || !filas.length) return null;
    const result = buildPrintHtml({
      filas,
      fechaDesde,
      fechaHasta,
      nombreEmpresa,
      empresa: selectedCompany,
      title,
      reportTitle: reportTitle || title,
    });
    if (!result) return null;
    if (typeof result === 'string') {
      return { bodyHtml: result, extraStyles: '', skipHeader: true };
    }
    const bodyHtml = result.bodyHtml || result.html || '';
    if (!bodyHtml) return null;
    return {
      bodyHtml,
      extraStyles: result.extraStyles || result.styles || '',
      skipHeader: result.skipHeader !== false,
    };
  }, [
    buildPrintHtml,
    filas,
    fechaDesde,
    fechaHasta,
    nombreEmpresa,
    selectedCompany,
    title,
    reportTitle,
  ]);

  const tableMinWidth = useMemo(() => {
    const n = Math.max((columns || []).length, 1);
    return { xs: Math.max(n * 240, 800), sm: Math.max(n * 280, 960) };
  }, [columns]);

  const generar = useCallback(async () => {
    if (!puedeLeer) return;
    if (!puedeModificar) {
      showError('No tiene permisos para generar este reporte');
      return;
    }
    if (!usuarioLog) {
      showError('Debe iniciar sesión para generar el reporte');
      return;
    }
    if (!puedeOperar) {
      showError('Seleccione una empresa con base de datos y sesión válida');
      return;
    }
    if (!fechaDesde || !fechaHasta) {
      showWarning('Indique fecha desde y fecha hasta');
      return;
    }
    if (fechaDesde > fechaHasta) {
      showWarning('La fecha desde no puede ser mayor que la fecha hasta');
      return;
    }

    setCargando(true);
    setInfoServicio('');
    try {
      const response = await fetch(`${servidor}/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credencialesBase(),
          fecha_desde: fechaDesde,
          fecha_hasta: fechaHasta,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setFilas([]);
        setGenerado(true);
        const msg =
          data.Error ||
          'El servicio de este reporte aún no está disponible o devolvió un error.';
        setInfoServicio(msg);
        if (response.status !== 404 && response.status !== 405) {
          showError(msg);
        } else {
          showWarning(msg);
        }
        return;
      }
      const rows =
        typeof mapResponseRows === 'function'
          ? mapResponseRows(data)
          : data.lineas || data.filas || data.cuentas || [];
      setFilas(Array.isArray(rows) ? rows : []);
      setGenerado(true);
      setInfoServicio('');
      showSuccess(
        Array.isArray(rows) && rows.length > 0
          ? `Reporte generado · ${rows.length} línea(s)`
          : 'Reporte generado sin movimientos en el período'
      );
    } catch {
      setFilas([]);
      setGenerado(true);
      const msg = 'Error de conexión al generar el reporte';
      setInfoServicio(msg);
      showError(msg);
    } finally {
      setCargando(false);
    }
  }, [
    puedeLeer,
    puedeModificar,
    puedeOperar,
    usuarioLog,
    servidor,
    endpoint,
    fechaDesde,
    fechaHasta,
    credencialesBase,
    mapResponseRows,
    showError,
    showSuccess,
    showWarning,
  ]);

  if (!puedeLeer) {
    return (
      <Box
        component="main"
        sx={{ minHeight: '100vh', bgcolor: FP.color.surface, p: { xs: 2, md: 3 } }}
      >
        <PageHeader
          title={title}
          subtitle={nombreNegocio ? `${nombreNegocio} · ${subtitle}` : subtitle}
        />
        <Card sx={{ ...cardShellSx, maxWidth: 560, mx: 'auto', mt: 4 }}>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            {IconComponent ? (
              <IconComponent sx={{ fontSize: 56, color: FP.color.textMuted, mb: 1 }} />
            ) : null}
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '1.05rem' }}>
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, mt: 0.5, fontSize: '0.92rem' }}>
              No tienes permisos para acceder a {title}.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (!puedeOperar) {
    return (
      <Box
        component="main"
        sx={{ minHeight: '100vh', bgcolor: FP.color.surface, p: { xs: 2, md: 3 } }}
      >
        <PageHeader
          title={title}
          subtitle={nombreNegocio ? `${nombreNegocio} · ${subtitle}` : subtitle}
        />
        <Card sx={{ ...cardShellSx, maxWidth: 560, mx: 'auto', mt: 4 }}>
          <CardContent sx={{ py: 3 }}>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              {!usuarioLog || !claveLog
                ? 'Debe iniciar sesión con usuario y contraseña válidos.'
                : 'Seleccione una empresa con base de datos asignada para consultar estados financieros.'}
            </Alert>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      component="main"
      sx={{ minHeight: '100vh', bgcolor: FP.color.surface, p: { xs: 2, md: 3 } }}
    >
      <PageHeader
        title={title}
        subtitle={[nombreNegocio, subtitle, etiquetaVersion].filter(Boolean).join(' · ')}
      />

      <Box>
        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SearchIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Período del reporte
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Box className="no-print" sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <PrintListado
                title={nombreEmpresa}
                reportTitle={reportTitle || title}
                subtitle={subtituloPrint}
                columns={columnasPrint}
                data={filas}
                emptyMessage={emptyMessage}
                printButtonLabel="Imprimir"
                printButtonProps={{
                  disabled: filas.length === 0 || !puedeAccionar,
                  sx: {
                    ...btnPrimarySx,
                    '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
                  },
                }}
                footer={footerTexto}
                bodyHtml={printDocumento?.bodyHtml}
                extraStyles={printDocumento?.extraStyles}
                skipHeader={Boolean(printDocumento?.skipHeader)}
              />
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={generar}
                disabled={cargando || !puedeAccionar}
                sx={{
                  ...btnOutlinedSx,
                  '&.Mui-disabled': { color: FP.color.textMuted },
                }}
              >
                Actualizar
              </Button>
              <Button
                variant="contained"
                startIcon={cargando ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                onClick={generar}
                disabled={cargando || !puedeAccionar}
                sx={btnPrimarySx}
              >
                Generar
              </Button>
            </Box>
          </Box>

          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Fecha desde"
                  type="date"
                  size="small"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={!puedeAccionar}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Fecha hasta"
                  type="date"
                  size="small"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={!puedeAccionar}
                  sx={fpInputSx}
                />
              </Grid>
            </Grid>
            {infoServicio ? (
              <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                {infoServicio}
              </Alert>
            ) : null}
          </CardContent>
        </Card>

        {typeof renderSummary === 'function' && generado && filas.length > 0 && !cargando
          ? renderSummary({ filas, fechaDesde, fechaHasta, nombreEmpresa })
          : null}

        <Card elevation={0} sx={cardShellSx}>
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: 1.2,
                  bgcolor: FP.color.accentBlueSoft,
                  color: FP.color.accentBlue,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {IconComponent ? <IconComponent sx={{ fontSize: 18 }} /> : null}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
                  {title}
                </Typography>
                <Typography
                  sx={{
                    color: FP.color.textSecondary,
                    fontSize: '0.78rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {fechaDesde && fechaHasta
                    ? `Período ${formatearFecha(fechaDesde)} — ${formatearFecha(fechaHasta)}`
                    : subtitle}
                </Typography>
              </Box>
            </Box>
            {cargando && (
              <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem', fontWeight: 600 }}>
                Cargando…
              </Typography>
            )}
          </Box>
          <CardContent
            sx={{
              p: filas.length > 0 && typeof renderBody === 'function' ? 0 : { xs: 1.5, sm: 2 },
              '&:last-child': {
                pb:
                  filas.length > 0 && typeof renderBody === 'function'
                    ? 0
                    : { xs: 1.5, sm: 2 },
              },
            }}
          >
            {cargando ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={28} sx={{ color: FP.color.accentBlue }} />
              </Box>
            ) : filas.length === 0 ? (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                {IconComponent ? (
                  <IconComponent sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                ) : null}
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                  No hay datos para mostrar
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }} textAlign="center" px={2}>
                  {generado
                    ? emptyMessage
                    : 'Seleccione el período y pulse «Generar» para consultar el reporte'}
                </Typography>
              </Box>
            ) : typeof renderBody === 'function' ? (
              renderBody({ filas, fechaDesde, fechaHasta, nombreEmpresa })
            ) : (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ border: `1px solid ${FP.color.border}`, borderRadius: 2, overflow: 'auto' }}
              >
                <Table sx={{ tableLayout: 'fixed', width: '100%', minWidth: tableMinWidth }}>
                  <colgroup>
                    {(columns || []).map((col) => (
                      <col
                        key={col.id}
                        style={{
                          width:
                            col.width ||
                            `${Math.floor(100 / Math.max((columns || []).length, 1))}%`,
                        }}
                      />
                    ))}
                  </colgroup>
                  <TableBody>
                    <TableRow
                      sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        '& td': {
                          border: 'none',
                          borderBottom: `2px solid ${FP.color.border}`,
                          bgcolor: FP.color.surfaceAlt,
                        },
                      }}
                    >
                      {(columns || []).map((col) => {
                        const align = col.align || 'left';
                        return (
                          <TableCell key={col.id}>
                            <Typography
                              component="span"
                              sx={
                                align === 'left'
                                  ? cellHeadSx
                                  : { ...cellHeadSx, textAlign: align, width: '100%' }
                              }
                            >
                              {col.label}
                            </Typography>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    {filas.map((row, idx) => (
                      <TableRow
                        key={row.id != null ? String(row.id) : `r-${idx}`}
                        hover
                        sx={{
                          bgcolor: row.es_total || row.es_titulo ? FP.color.surfaceAlt : 'inherit',
                          '& td': {
                            fontWeight: row.es_total ? 700 : row.es_titulo ? 600 : 400,
                          },
                        }}
                      >
                        {(columns || []).map((col) => {
                          const raw = row[col.id];
                          const content =
                            typeof col.render === 'function' ? col.render(raw, row) : raw ?? '—';
                          const cellTitle =
                            typeof content === 'string' || typeof content === 'number'
                              ? String(content)
                              : raw != null && raw !== ''
                                ? String(raw)
                                : '';
                          const align = col.align || 'left';
                          const esTexto = align === 'left';
                          return (
                            <TableCell
                              key={col.id}
                              align={align}
                              sx={
                                esTexto
                                  ? cellEllipsisSx
                                  : undefined
                              }
                              title={esTexto && cellTitle ? cellTitle : undefined}
                            >
                              {content}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {!hideFooter && footerTexto && filas.length > 0 ? (
              <Typography
                sx={{
                  mt: 2,
                  fontWeight: 700,
                  color: FP.color.textPrimary,
                  fontSize: '0.9rem',
                  textAlign: 'right',
                }}
              >
                {footerTexto}
              </Typography>
            ) : null}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default EstadoFinancieroShell;
export { fmtMoney, formatearFecha, formatearFechaLarga, fmtMontoContable };
export { textoPeriodoCorte, textoPeriodoRango } from './estadosFinancierosShared';
